// The dashboard's sections, shared by the desktop sidebar and the phone navigation
// (DashboardMobileNav). People mostly either sell or buy, so sections are grouped by side;
// Overview and Messages belong to both.

export const SELLING = 'selling';
export const BUYING = 'buying';
const SIDE_STORAGE_KEY = 'dashboardSide';
const SIDE_CHANGE_EVENT = 'dashboard-side-change';

export const OVERVIEW_LINK = {
  key: 'overview',
  name: 'DashboardPage',
  labelId: 'DashboardNav.overview',
  icon: 'home',
};

export const MESSAGES_LINK = {
  key: 'messages',
  name: 'MessagesPage',
  params: { tab: 'enquiry' },
  labelId: 'DashboardNav.messages',
  icon: 'messages',
};

export const SIDE_LINKS = {
  [SELLING]: [
    {
      key: 'sales',
      name: 'InboxPage',
      params: { tab: 'sales' },
      labelId: 'DashboardNav.orders',
      icon: 'orders',
    },
    { key: 'offers', name: 'OffersPage', labelId: 'DashboardNav.offers', icon: 'offers' },
    {
      key: 'listings',
      name: 'ManageListingsPage',
      labelId: 'DashboardNav.listings',
      icon: 'listings',
    },
    {
      key: 'shop',
      name: 'StorePage',
      labelId: 'DashboardNav.shop',
      icon: 'shop',
      isOwnStore: true,
    },
    { key: 'payouts', name: 'PayoutsPage', labelId: 'DashboardNav.payouts', icon: 'payouts' },
  ],
  [BUYING]: [
    {
      key: 'purchases',
      name: 'InboxPage',
      params: { tab: 'orders' },
      labelId: 'DashboardNav.purchases',
      icon: 'orders',
    },
    {
      key: 'offersSent',
      name: 'OffersSentPage',
      labelId: 'DashboardNav.offers',
      icon: 'offers',
    },
    {
      key: 'favorites',
      name: 'FavoritesPage',
      labelId: 'DashboardNav.favorites',
      icon: 'favorites',
    },
  ],
};

/**
 * Which side a page belongs to, from its path: 'selling', 'buying', or null for pages that
 * belong to both (Overview, Messages, settings).
 */
export const getSideForPath = (pathname = '', ownStorePaths = []) => {
  if (
    pathname.startsWith('/inbox/sales') ||
    pathname.startsWith('/sale/') ||
    pathname === '/offers' ||
    pathname === '/listings' ||
    pathname.startsWith('/payouts') ||
    ownStorePaths.includes(pathname)
  ) {
    return SELLING;
  }
  if (
    pathname.startsWith('/inbox/orders') ||
    pathname.startsWith('/order/') ||
    pathname.startsWith('/offers/sent') ||
    pathname.startsWith('/wishlist')
  ) {
    return BUYING;
  }
  return null;
};

/** Whether a section link is the page being shown. */
export const isLinkCurrent = (link, pathname = '', ownStorePaths = []) => {
  switch (link.key) {
    case 'overview':
      return pathname === '/dashboard';
    case 'sales':
      return pathname.startsWith('/inbox/sales') || pathname.startsWith('/sale/');
    case 'purchases':
      return pathname.startsWith('/inbox/orders') || pathname.startsWith('/order/');
    case 'offers':
      return pathname === '/offers';
    case 'offersSent':
      return pathname.startsWith('/offers/sent');
    case 'listings':
      return pathname === '/listings';
    case 'shop':
      return ownStorePaths.includes(pathname);
    case 'payouts':
      return pathname.startsWith('/payouts');
    case 'favorites':
      return pathname.startsWith('/wishlist');
    case 'messages':
      return pathname.startsWith('/messages');
    default:
      return false;
  }
};

/** The side last chosen on this device, or null. */
export const getStoredSide = () => {
  try {
    const side = window.localStorage.getItem(SIDE_STORAGE_KEY);
    return side === SELLING || side === BUYING ? side : null;
  } catch (e) {
    return null;
  }
};

/** Remember the chosen side and tell other dashboard components on the page. */
export const storeSide = side => {
  try {
    window.localStorage.setItem(SIDE_STORAGE_KEY, side);
  } catch (e) {
    // Private browsing: the choice just isn't remembered
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SIDE_CHANGE_EVENT, { detail: side }));
  }
};

export const onSideChange = callback => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const handler = e => callback(e.detail);
  window.addEventListener(SIDE_CHANGE_EVENT, handler);
  return () => window.removeEventListener(SIDE_CHANGE_EVENT, handler);
};
